import { useEffect, useState, useCallback } from 'react';
import { Loader2, Video, PhoneOff, Play, Clock, Users, Download } from 'lucide-react';
import { apiClient } from '@/lib/api';
import type { Workgroup, WorkgroupHuddle } from './types';

interface Props {
  workgroup: Workgroup;
  currentUserId: string | null;
}

interface StartResponse {
  huddle: WorkgroupHuddle;
  room: { id: string; unique_name: string; title: string; meeting_link: string | null };
}

interface RecordingUrlResponse {
  status: 'processing' | 'completed' | 'failed' | 'unavailable';
  url: string | null;
}

function fmtDuration(sec: number | null | undefined): string {
  if (!sec || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function HuddleTab({ workgroup, currentUserId }: Props) {
  const [active, setActive] = useState<WorkgroupHuddle | null>(null);
  const [history, setHistory] = useState<WorkgroupHuddle[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [recordOnStart, setRecordOnStart] = useState(false);
  const [recordingLoadingId, setRecordingLoadingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        apiClient.get<WorkgroupHuddle | null>(`/messenger/workgroups/${workgroup.id}/huddle/active`),
        apiClient.get<WorkgroupHuddle[]>(`/messenger/workgroups/${workgroup.id}/huddle/history?limit=20`),
      ]);
      setActive(a.data ?? null);
      setHistory(h.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  async function startHuddle() {
    setStarting(true);
    try {
      const res = await apiClient.post<StartResponse>(
        `/messenger/workgroups/${workgroup.id}/huddle/start`,
        { record: recordOnStart },
      );
      if (res.data?.room.meeting_link) {
        window.open(res.data.room.meeting_link, '_blank', 'noopener,noreferrer');
      }
      load();
    } finally {
      setStarting(false);
    }
  }

  async function endHuddle(huddleId: string) {
    await apiClient.post(`/messenger/workgroups/${workgroup.id}/huddle/${huddleId}/end`, {});
    load();
  }

  function joinActive() {
    if (active?.meeting_link) {
      window.open(active.meeting_link, '_blank', 'noopener,noreferrer');
    }
  }

  async function downloadRecording(huddleId: string) {
    setRecordingLoadingId(huddleId);
    try {
      const res = await apiClient.get<RecordingUrlResponse>(
        `/messenger/workgroups/${workgroup.id}/huddle/${huddleId}/recording`,
      );
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const status = res.data?.status ?? 'unavailable';
        const msg = status === 'processing'
          ? 'La registrazione è ancora in elaborazione. Riprova tra qualche secondo.'
          : status === 'failed'
            ? 'La registrazione non è disponibile (errore upstream).'
            : 'Registrazione non disponibile.';
        alert(msg);
      }
    } finally {
      setRecordingLoadingId(null);
    }
  }

  function recBadge(h: WorkgroupHuddle): { label: string; cls: string } | null {
    if (!h.recording_requested) return null;
    if (h.recording_available) return { label: 'REC pronta', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' };
    if (h.recording_status === 'failed') return { label: 'REC errore', cls: 'text-red-300 border-red-500/40 bg-red-500/10' };
    if (h.recording_status === 'processing' || h.recording_status === null) {
      return { label: 'REC in elaborazione', cls: 'text-amber-300 border-amber-500/40 bg-amber-500/10' };
    }
    return null;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Active huddle banner */}
      {active ? (
        <div className="bg-gradient-to-r from-emerald-600/30 to-teal-600/30 border border-emerald-500/40 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/30 flex items-center justify-center">
              <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="text-foreground font-semibold flex items-center gap-2">
                Video Call Live in corso
                {active.recording_requested && (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/15 text-red-200">
                    ● REC
                  </span>
                )}
              </div>
              <div className="text-xs text-emerald-200">
                Avviato da {active.started_by_name ?? '—'} · {new Date(active.started_at).toLocaleTimeString('it-IT')}
              </div>
            </div>
            <button
              onClick={joinActive}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-medium flex items-center gap-2"
            >
              <Video size={14} /> Unisciti
            </button>
            {(active.started_by === currentUserId) && (
              <button
                onClick={() => endHuddle(active.id)}
                className="px-3 py-2 bg-red-600/80 hover:bg-red-500 rounded-lg text-white text-sm flex items-center gap-1"
                title="Chiudi Video Call Live"
              >
                <PhoneOff size={14} />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={startHuddle}
            disabled={starting}
            className="w-full p-6 bg-gradient-to-br from-blue-600 to-purple-700 hover:from-blue-500 hover:to-purple-600 disabled:opacity-50 rounded-2xl text-white flex flex-col items-center justify-center gap-2 transition"
          >
            {starting ? <Loader2 size={32} className="animate-spin" /> : <Play size={32} />}
            <span className="text-lg font-semibold">Avvia Video Call Live</span>
            <span className="text-xs text-blue-100">Chiamata video 1-click per tutto il gruppo</span>
          </button>
          <label className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={recordOnStart}
              onChange={(e) => setRecordOnStart(e.target.checked)}
              className="rounded border-border accent-red-500"
            />
            <span className="flex items-center gap-1.5">
              Registra questa Video Call Live
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-300">
                REC
              </span>
            </span>
            <span className="text-xs text-muted-foreground/70 ml-auto">audio + video, salvato su R2</span>
          </label>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
          <Clock size={12} /> Cronologia
        </h3>
        {loading && <div className="text-center py-4 text-muted-foreground"><Loader2 className="animate-spin inline" /></div>}
        {!loading && history.length === 0 && (
          <div className="text-center py-6 text-muted-foreground/80 text-sm">Nessuna Video Call Live precedente</div>
        )}
        <div className="space-y-1">
          {history.filter(h => h.ended_at !== null || h.id !== active?.id).map(h => {
            const badge = recBadge(h);
            return (
              <div key={h.id} className="bg-card border border-border rounded-lg px-3 py-2 flex items-center gap-3 text-sm">
                <Video size={14} className="text-muted-foreground/80" />
                <div className="flex-1 min-w-0">
                  <div className="text-foreground truncate">
                    {h.started_by_name ?? '—'}
                    <span className="text-muted-foreground/80"> · {new Date(h.started_at).toLocaleString('it-IT')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users size={11} /> {h.participant_count}</span>
                  <span>{fmtDuration(h.duration_seconds)}</span>
                  {badge && (
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${badge.cls}`}>
                      {badge.label}
                    </span>
                  )}
                  {h.recording_available && (
                    <button
                      onClick={() => downloadRecording(h.id)}
                      disabled={recordingLoadingId === h.id}
                      className="text-blue-400 hover:text-blue-300 disabled:opacity-50 flex items-center gap-1"
                      title="Scarica registrazione"
                    >
                      {recordingLoadingId === h.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Scarica
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
